import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Pencil, Trash2, Briefcase } from 'lucide-react';
import { atlasApi, AtlasUser, Cargo, PerfilDisc, DiscFator } from '../shared/atlasApi';
import FeedbackBanner, { Feedback } from '../shared/FeedbackBanner';
import { inputCls, labelCls } from '../shared/ListaEditavel';

interface Props {
  user: AtlasUser;
  podeEditar: boolean;
}

const DISC_CORES: Record<DiscFator, string> = { D: '#e11d48', I: '#f59e0b', S: '#10b981', C: '#3b82f6' };
const DISC_LABEL: Record<DiscFator, string> = { D: 'Dominância', I: 'Influência', S: 'Estabilidade', C: 'Conformidade' };
const FATORES: DiscFator[] = ['D', 'I', 'S', 'C'];

const toCsv = (arr: string[]) => (arr || []).join(', ');
const fromCsv = (s: string) => s.split(',').map(x => x.trim()).filter(Boolean);
const clamp = (n: number) => Math.max(0, Math.min(100, Math.round(n) || 0));

interface FormState {
  codigo: string;
  nome: string;
  departamento: string;
  nivel: number;
  descricao: string;
  responsabilidades: string;
  requisitos: string;
  perfil: PerfilDisc;
}

const vazio = (): FormState => ({
  codigo: '', nome: '', departamento: '', nivel: 1, descricao: '',
  responsabilidades: '', requisitos: '',
  perfil: { D: 50, I: 50, S: 50, C: 50 },
});

// barra resumo do perfil ideal
const PerfilResumo: React.FC<{ perfil: PerfilDisc }> = ({ perfil }) => (
  <div className="flex items-center gap-1.5">
    {FATORES.map(f => (
      <div key={f} className="flex flex-col items-center gap-0.5">
        <div className="w-7 h-12 bg-gray-100 rounded relative overflow-hidden flex items-end" title={`${DISC_LABEL[f]}: ${perfil?.[f] ?? 0}`}>
          <div className="w-full" style={{ height: `${perfil?.[f] ?? 0}%`, backgroundColor: DISC_CORES[f] }} />
        </div>
        <span className="text-[10px] font-bold text-gray-500">{f}</span>
      </div>
    ))}
  </div>
);

const PerfisCargo: React.FC<Props> = ({ user, podeEditar }) => {
  const [itens, setItens] = useState<Cargo[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [modalAberto, setModalAberto] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(vazio());
  const [salvando, setSalvando] = useState(false);
  const [fb, setFb] = useState<Feedback>(null);

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    atlasApi.getCargos(user)
      .then(r => setItens(r.itens || []))
      .catch(e => setErro(e.message || 'Erro ao carregar cargos'))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirNova = () => {
    setEditId(null);
    setForm(vazio());
    setFb(null);
    setModalAberto(true);
  };

  const abrirEdicao = (c: Cargo) => {
    setEditId(c._id);
    setForm({
      codigo: c.codigo || '',
      nome: c.nome || '',
      departamento: c.departamento || '',
      nivel: c.nivel ?? 1,
      descricao: c.descricao || '',
      responsabilidades: toCsv(c.responsabilidades || []),
      requisitos: toCsv(c.requisitos || []),
      perfil: {
        D: c.perfil_disc_ideal?.D ?? 50,
        I: c.perfil_disc_ideal?.I ?? 50,
        S: c.perfil_disc_ideal?.S ?? 50,
        C: c.perfil_disc_ideal?.C ?? 50,
      },
    });
    setFb(null);
    setModalAberto(true);
  };

  const salvar = async () => {
    if (!form.codigo.trim()) { setFb({ tipo: 'erro', msg: 'Código é obrigatório.' }); return; }
    if (!form.nome.trim()) { setFb({ tipo: 'erro', msg: 'Nome é obrigatório.' }); return; }
    setSalvando(true);
    setFb(null);
    const body = {
      codigo: form.codigo.trim(),
      nome: form.nome.trim(),
      departamento: form.departamento.trim() || undefined,
      nivel: Number(form.nivel) || 1,
      descricao: form.descricao.trim() || undefined,
      responsabilidades: fromCsv(form.responsabilidades),
      requisitos: fromCsv(form.requisitos),
      perfil_disc_ideal: {
        D: clamp(form.perfil.D), I: clamp(form.perfil.I),
        S: clamp(form.perfil.S), C: clamp(form.perfil.C),
      },
    };
    try {
      if (editId) await atlasApi.editarCargo(user, editId, body);
      else await atlasApi.criarCargo(user, body as Omit<Cargo, '_id' | 'empresaId'>);
      setModalAberto(false);
      carregar();
    } catch (e: any) {
      setFb({ tipo: 'erro', msg: e.message || 'Erro ao salvar.' });
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (c: Cargo) => {
    if (!window.confirm(`Excluir o cargo "${c.nome}"?`)) return;
    try {
      await atlasApi.excluirCargo(user, c._id);
      carregar();
    } catch (e: any) {
      setErro(e.message || 'Erro ao excluir.');
    }
  };

  const setFator = (f: DiscFator, v: number) =>
    setForm(p => ({ ...p, perfil: { ...p.perfil, [f]: clamp(v) } }));

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 flex-1">
          <Briefcase className="w-5 h-5 text-purple-600" /> Perfis de cargo
        </h2>
        {podeEditar && (
          <button onClick={abrirNova} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium flex items-center gap-2">
            <Plus className="w-4 h-4" /> Novo cargo
          </button>
        )}
      </div>

      {erro && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>}

      {loading ? (
        <div className="p-12 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...</div>
      ) : itens.length === 0 ? (
        <div className="p-12 text-center text-gray-400 bg-white rounded-2xl border border-gray-200">Nenhum cargo cadastrado.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {itens.map(c => (
            <div key={c._id} className="bg-white rounded-2xl border border-gray-200 p-5 flex gap-4">
              <PerfilResumo perfil={c.perfil_disc_ideal} />
              <div className="flex-1 min-w-0 flex flex-col gap-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-gray-900">{c.nome}</h3>
                  <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{c.codigo}</span>
                  <span className="text-xs px-1.5 py-0.5 rounded bg-purple-50 text-purple-600">nível {c.nivel}</span>
                </div>
                {c.departamento && <p className="text-xs text-gray-400">Depto: {c.departamento}</p>}
                {c.descricao && <p className="text-sm text-gray-500 line-clamp-2">{c.descricao}</p>}
                {podeEditar && (
                  <div className="flex items-center gap-2 mt-auto pt-2">
                    <button onClick={() => abrirEdicao(c)} className="text-xs text-purple-600 hover:underline flex items-center gap-1">
                      <Pencil className="w-3.5 h-3.5" /> Editar
                    </button>
                    <button onClick={() => excluir(c)} className="text-xs text-rose-600 hover:underline flex items-center gap-1 ml-auto">
                      <Trash2 className="w-3.5 h-3.5" /> Excluir
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {modalAberto && (
        <div className="fixed inset-0 z-[10050] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setModalAberto(false)}>
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="font-bold text-gray-900 flex-1">{editId ? 'Editar cargo' : 'Novo cargo'}</h3>
              <button onClick={() => setModalAberto(false)} className="p-2 rounded-lg text-gray-400 hover:bg-gray-100">✕</button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className={labelCls}>Código *</label>
                  <input className={inputCls} value={form.codigo} onChange={e => setForm(p => ({ ...p, codigo: e.target.value }))} placeholder="VEND01" />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Nome *</label>
                  <input className={inputCls} value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} placeholder="Vendedor" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Departamento</label>
                  <input className={inputCls} value={form.departamento} onChange={e => setForm(p => ({ ...p, departamento: e.target.value }))} placeholder="Comercial" />
                </div>
                <div>
                  <label className={labelCls}>Nível</label>
                  <input className={inputCls} type="number" min={1} value={form.nivel} onChange={e => setForm(p => ({ ...p, nivel: Number(e.target.value) }))} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Descrição</label>
                <textarea className={`${inputCls} min-h-[60px] resize-y`} value={form.descricao} onChange={e => setForm(p => ({ ...p, descricao: e.target.value }))} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Responsabilidades (separadas por vírgula)</label>
                  <input className={inputCls} value={form.responsabilidades} onChange={e => setForm(p => ({ ...p, responsabilidades: e.target.value }))} placeholder="Prospecção, Negociação" />
                </div>
                <div>
                  <label className={labelCls}>Requisitos (separados por vírgula)</label>
                  <input className={inputCls} value={form.requisitos} onChange={e => setForm(p => ({ ...p, requisitos: e.target.value }))} placeholder="Ensino médio, CNH B" />
                </div>
              </div>

              {/* Perfil DISC ideal */}
              <div className="pt-2">
                <label className={labelCls}>Perfil DISC ideal (0–100)</label>
                <div className="space-y-3 mt-1">
                  {FATORES.map(f => (
                    <div key={f} className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0" style={{ backgroundColor: DISC_CORES[f] }}>{f}</span>
                      <span className="text-xs text-gray-500 w-24 flex-shrink-0">{DISC_LABEL[f]}</span>
                      <input
                        type="range" min={0} max={100} value={form.perfil[f]}
                        onChange={e => setFator(f, Number(e.target.value))}
                        className="flex-1 accent-purple-600"
                      />
                      <input
                        type="number" min={0} max={100} value={form.perfil[f]}
                        onChange={e => setFator(f, Number(e.target.value))}
                        className="w-16 px-2 py-1 rounded-lg border border-gray-200 text-sm text-gray-800 text-center focus:outline-none focus:ring-2 focus:ring-purple-200"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <FeedbackBanner feedback={fb} />

              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setModalAberto(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
                <button onClick={salvar} disabled={salvando} className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg">
                  {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerfisCargo;
